import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { ShoppingBag, Quote, BookOpen, FileText, Newspaper, ExternalLink } from "lucide-react";

const KIND_META = {
  book:    { label: "Libro",    Icon: BookOpen,   btnIcon: ShoppingBag },
  article: { label: "Articolo", Icon: Newspaper,  btnIcon: ExternalLink },
  ebook:   { label: "Ebook",    Icon: FileText,   btnIcon: ShoppingBag },
};

export default function Libri() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/books").then((r) => setBooks(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="container-editorial py-16 lg:py-24" data-testid="page-libri">
      <div className="max-w-3xl">
        <span className="pill">Pubblicazioni</span>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95]">Le mie pubblicazioni.</h1>
        <p className="mt-6 text-lg text-viola/75">Libri, articoli e guide di divulgazione scientifica su sessualità, relazioni non normative, identità e cura.</p>
      </div>

      {loading ? (
        <p className="mt-16 text-viola/60" data-testid="books-loading">Caricamento…</p>
      ) : books.length === 0 ? (
        <div className="mt-16 rounded-2xl bg-lilla p-12" data-testid="books-empty">
          <p className="font-display text-2xl">In arrivo.</p>
          <p className="mt-3 text-viola/70">Le prime pubblicazioni saranno annunciate presto — iscriviti alla newsletter per essere avvisatə.</p>
        </div>
      ) : (
        <div className="mt-16 space-y-24">
          {books.map((b, i) => {
            const meta = KIND_META[b.kind] || KIND_META.book;
            const KindIcon = meta.Icon;
            const BtnIcon = meta.btnIcon;
            return (
            <div key={b.id} className={`grid lg:grid-cols-12 gap-10 items-start ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`} data-testid={`book-${b.id}`}>
              <div className="lg:col-span-5">
                <div className="aspect-[3/4] overflow-hidden rounded-xl bg-lilla shadow-[0_30px_60px_-30px_rgba(44,20,69,0.35)]">
                  {b.cover_url ? <img src={b.cover_url} alt={b.title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-arancio/60 to-fucsia/40" />}
                </div>
                {b.cover_credits && (
                  <p className="mt-2 text-xs text-viola/60 italic text-center">{b.cover_credits}</p>
                )}
              </div>
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-viola text-cream px-3 py-1 text-[10px] font-semibold uppercase tracking-widest">
                  <KindIcon size={11} /> {meta.label}
                </span>
                {b.year && <p className="mt-3 text-xs uppercase tracking-widest text-viola/60 font-semibold">{b.year}</p>}
                <h2 className="mt-2 font-display text-4xl sm:text-5xl tracking-tighter leading-none">{b.title}</h2>
                {b.subtitle && <p className="mt-3 text-lg italic text-viola/70">{b.subtitle}</p>}
                <p className="mt-6 text-viola/80 leading-relaxed">{b.description}</p>

                {b.excerpt && (
                  <figure className="mt-8 rounded-xl bg-arancio/30 border border-viola/10 p-6" data-testid={`book-excerpt-${b.id}`}>
                    <Quote className="text-fucsia" size={20} />
                    <blockquote className="mt-3 font-display italic text-lg">{b.excerpt}</blockquote>
                    {(b.excerpt_author || b.excerpt_source) && (
                      <figcaption className="mt-4 text-sm text-viola/70 not-italic">
                        — {b.excerpt_author}
                        {b.excerpt_source && (
                          <>
                            , <span className="italic">«{b.excerpt_source}»</span>
                          </>
                        )}
                      </figcaption>
                    )}
                  </figure>
                )}

                {b.reviews?.length > 0 && (
                  <div className="mt-8 space-y-4">
                    {b.reviews.map((r) => (
                      <blockquote key={`${r.author}-${(r.text || "").slice(0, 20)}`} className="border-l-2 border-fucsia pl-4">
                        <p className="text-viola/85 italic">"{r.text}"</p>
                        <footer className="mt-2 text-sm text-viola/60">— {r.author}{r.source ? `, ${r.source}` : ""}</footer>
                      </blockquote>
                    ))}
                  </div>
                )}

                {b.buy_links?.length > 0 && (
                  <div className="mt-8 flex flex-wrap gap-3">
                    {b.buy_links.map((l, idx) => (
                      <a key={l.url || `${l.label}-${idx}`} href={l.url} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid={`buy-${b.id}-${idx}`}>
                        <BtnIcon size={16} /> {l.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}
